import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack, Paper, Typography, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const TaxSummaryReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/tax-summary-report${queryString ? `?${queryString}` : ''}`
      );

      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        const payableReceivableTotal =
          response.data.summary.difference.gst + response.data.summary.difference.qst;
        
        if (exportType === 'excel') {
          const excelData = [
            {
              "Category": "Paid",
              "GST Amount": response.data.summary.paid.gst,
              "QST Amount": response.data.summary.paid.qst,
              "HST Amount": response.data.summary.paid.hst,
              "Total Amount": response.data.summary.paid.total
            },
            {
              "Category": "Received",
              "GST Amount": response.data.summary.received.gst,
              "QST Amount": response.data.summary.received.qst,
              "HST Amount": response.data.summary.received.hst,
              "Total Amount": response.data.summary.received.total
            },
            {
              "Category": "Difference",
              "GST Amount": response.data.summary.difference.gst,
              "QST Amount": response.data.summary.difference.qst,
              "HST Amount": response.data.summary.difference.hst,
              "Total Amount": ""
            },
            {
              "Category": "Payable/Receivable",
              "GST Amount": "",
              "QST Amount": "",
              "HST Amount": "",
              "Total Amount": payableReceivableTotal
            }
          ];
          
          exportToExcel(excelData, `Tax_Summary_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Tax Summary Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 15px; }
              .report-filters { font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Tax Summary Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>GST Amount</th>
                  <th>QST Amount</th>
                  <th>HST Amount</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Paid</td>
                  <td>${response.data.summary.paid.gst}</td>
                  <td>${response.data.summary.paid.qst}</td>
                  <td>${response.data.summary.paid.hst || 0}</td>

                  <td>${response.data.summary.paid.total}</td>
                </tr>
                <tr>
                  <td>Received</td>
                  <td>${response.data.summary.received.gst}</td>
                  <td>${response.data.summary.received.qst}</td>
                  <td>${response.data.summary.received.hst || 0}</td>
                  <td>${response.data.summary.received.total}</td>
                </tr>
                <tr class="category-header">
                  <td>Difference</td>
                  <td>${response.data.summary.difference.gst}</td>
                  <td>${response.data.summary.difference.qst}</td>
                  <td>${response.data.summary.difference.hst || 0}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Payable/Receivable</td>
                  <td>${payableReceivableTotal}</td>
                  <td></td>
                  <td></td>
                </tr>
                
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Tax_Summary_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        if (exportType === 'html') {
          const reportWindow = window.open('', '_blank');
          if (!reportWindow) {
            toast.error("Please allow popups for this site to view reports");
            return;
          }
          
          reportWindow.document.write(htmlContent);
          reportWindow.document.close();
        }
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating tax summary report:", error);
      toast.error("Failed to generate tax summary report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
      if (filters.dateTo) params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/tax-summary-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        const payableReceivableTotal =
          response.data.summary.difference.gst + response.data.summary.difference.qst;
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Tax Summary Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .category-header { font-weight: bold; background-color: #f8f9fa; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Tax Summary Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>GST Amount</th>
                  <th>QST Amount</th>
                  <th>HST Amount</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Paid</td>
                  <td>${response.data.summary.paid.gst}</td>
                  <td>${response.data.summary.paid.qst}</td>
                  <td>${response.data.summary.paid.hst || 0}</td>
                  <td>${response.data.summary.paid.total}</td>
                </tr>
                <tr>
                  <td>Received</td>
                  <td>${response.data.summary.received.gst}</td>
                  <td>${response.data.summary.received.qst}</td>
                  <td>${response.data.summary.received.hst || 0}</td>
                  <td>${response.data.summary.received.total}</td>
                </tr>
                <tr class="category-header">
                  <td>Difference</td>
                  <td>${response.data.summary.difference.gst}</td>
                  <td>${response.data.summary.difference.qst}</td>
                  <td>${response.data.summary.difference.hst || 0}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Payable/Receivable</td>
                  <td>${payableReceivableTotal}</td>
                  <td></td>
                  <td></td>
                </tr>
                
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Tax_Summary_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  const SummaryCard = ({ title, data, backgroundColor }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        backgroundColor,
        height: '100%'
      }}
    >
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box>
        <Typography variant="body1">GST: ${data?.gst || 0}</Typography>
        <Typography variant="body1">QST: ${data?.qst || 0}</Typography>
        <Typography variant="body1" fontWeight="bold">Total: ${data?.total || 0}</Typography>
      </Box>
    </Paper>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={5}>
            <DatePicker
              label="From Date"
              value={filters.dateFrom}
              onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <DatePicker
              label="To Date"
              value={filters.dateTo}
              onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Stack spacing={1}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => generateReport('html')}
                disabled={loadingReport || sendingEmail}
                startIcon={<DescriptionIcon />}
                sx={{ 
                  height: "38px", 
                  backgroundColor: colors.primary, 
                  '&:hover': { 
                    backgroundColor: colors.primary, 
                    opacity: 0.9 
                  } 
                }}
              >
                {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('pdf')}
                disabled={loadingReport || sendingEmail}
                startIcon={<PictureAsPdfIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('excel')}
                disabled={loadingReport || sendingEmail}
                startIcon={<FileDownloadIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('email')}
                disabled={loadingReport || sendingEmail}
                startIcon={<EmailIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                Email
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default TaxSummaryReport;
